import React, { useCallback, useEffect, useState } from 'react';
import { fetchClientDebugDeviceTimeline, fetchClientDebugJobTimeline } from '../api/restapi';
import {
    OUTCOME_LABELS, asList, formatDuration, formatOffset, formatTimestamp, outcomeNote, outcomeOf, playbookLabel, reasonLabel, stageLabel, titleCase,
} from '../utils/clientDebugFormat';
import '../assets/ClientDebugTimeline.css';

const RANGES = [7, 30, 90];

const STATUS_EVENTS = {
    WAITING_FOR_LOGS: { title: 'Waiting for logs', tone: 'warning' },
    QUEUED: { title: 'Queued for analysis', tone: 'info' },
    RUNNING: { title: 'Analysis started', tone: 'info' },
    COMPLETED: { title: 'Succeeded', tone: 'success' },
    PARTIAL: { title: 'Succeeded with partial analysis', tone: 'warning' },
    NOT_DEBUGGABLE: { title: 'Failed: no logs found', tone: 'danger' },
    FAILED: { title: 'Failed', tone: 'danger' },
};

const STAGE_TONES = { COMPLETED: 'success', FAILED: 'danger', RUNNING: 'info', SKIPPED: 'neutral' };

/** What to show for one timeline event: a title, an optional second line and a tone for the dot. */
function describeEvent(event) {
    switch (event.kind) {
        case 'issue':
            return { title: 'Issue occurred on the device', tone: 'neutral' };
        case 'requested':
            return {
                title: 'Debug requested',
                note: event.status === 'WAITING_FOR_LOGS' ? 'waiting for logs'
                    : event.status === 'QUEUED' ? 'logs supplied, queued for analysis' : null,
                tone: 'info',
            };
        case 'status':
            return {
                ...(STATUS_EVENTS[event.status] || { title: titleCase(event.status || 'Status change'), tone: 'neutral' }),
                note: reasonLabel(event.detail),
            };
        case 'stage':
            return {
                title: stageLabel(event.name),
                note: [event.status === 'SKIPPED' ? 'skipped' : null, event.detail, event.errorCode].filter(Boolean).join(' · ') || null,
                tone: STAGE_TONES[event.status] || 'neutral',
            };
        case 'email':
            return event.status === 'FAILED'
                ? { title: 'Email send failure', note: reasonLabel(event.detail), tone: 'danger' }
                : { title: 'Email sent', note: reasonLabel(event.detail), tone: 'success' };
        default:
            return { title: titleCase(event.kind || 'Event'), tone: 'neutral' };
    }
}

const toMs = (value) => {
    const ms = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(ms) ? ms : null;
};

/**
 * The lifecycle of one debug as a vertical timeline: each event with its time and offset from the request, and for
 * pipeline stages a bar placed on a shared track (start and length relative to the whole debug).
 */
export function JobTimeline({ timeline }) {
    const events = asList(timeline?.events);
    if (events.length === 0) return <p className="client-debug-muted">No lifecycle events recorded.</p>;

    const requestedAt = toMs(timeline.createdAt) ?? toMs(events.find((e) => e.kind === 'requested')?.at) ?? toMs(events[0].at);
    const ends = events.flatMap((e) => [toMs(e.at), toMs(e.endedAt)]).concat([toMs(timeline.finishedAt)]).filter((v) => v != null);
    const span = Math.max(1, Math.max(...ends) - requestedAt);

    return (
        <ol className="cdt-timeline" aria-label="Debug timeline">
            {events.map((event, index) => {
                const { title, note, tone } = describeEvent(event);
                const at = toMs(event.at);
                const isStage = event.kind === 'stage';
                const end = toMs(event.endedAt);
                const length = isStage ? (event.durationMs ?? (end != null && at != null ? end - at : 0)) : 0;
                return (
                    <li className={`cdt-event ${tone} ${event.kind}`} key={`${event.kind}-${event.at}-${index}`}>
                        <div className="cdt-when">
                            {formatTimestamp(event.at)}
                            {at != null && requestedAt != null && <small>{formatOffset(at - requestedAt)}</small>}
                        </div>
                        <div className="cdt-dot" />
                        <div className="cdt-what">
                            <strong>{title}</strong>
                            {(note || (isStage && event.durationMs != null)) && (
                                <span>{[note, isStage && event.durationMs != null ? formatDuration(event.durationMs) : null].filter(Boolean).join(' · ')}</span>
                            )}
                        </div>
                        <div className="cdt-track" aria-hidden="true">
                            {isStage && at != null && (
                                <div
                                    className="cdt-bar"
                                    style={{
                                        left: `${Math.min(98, Math.max(0, ((at - requestedAt) / span) * 100))}%`,
                                        width: `${Math.min(100, Math.max(1.5, (length / span) * 100))}%`,
                                    }}
                                />
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

/** The logs a debug picked up. Generic wording: a crash dump, or a log. */
export function LogsPickedUp({ logs }) {
    const items = asList(logs);
    return (
        <div className="cdt-logs" aria-label="Logs picked up">
            {items.length === 0 ? <p className="client-debug-muted">No logs were picked up.</p> : items.map((log, index) => (
                <div className="cdt-log" key={`${log.artifactType}-${log.collectedAt}-${index}`}>
                    <strong>{log.artifactType === 'CRASH_DUMP' ? 'Crash dump' : 'Log'}</strong>
                    <span>from {formatTimestamp(log.collectedAt)}</span>
                    <span className={`cdt-chip ${log.staged ? 'yes' : 'no'}`}>{log.staged ? 'Retrieved' : 'Not retrieved'}</span>
                    <span className={`cdt-chip ${log.analyzed ? 'yes' : 'no'}`}>{log.analyzed ? 'Analyzed' : 'Not analyzed'}</span>
                </div>
            ))}
        </div>
    );
}

/** A debug's timeline, loaded when first shown. */
export function JobTimelineSection({ userInfo, jobId }) {
    const [state, setState] = useState({ loading: true, timeline: null, error: null });
    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, timeline: null, error: null });
        fetchClientDebugJobTimeline({ userEmail: userInfo, jobId }).then((result) => {
            if (!cancelled) setState({ loading: false, timeline: result.timeline, error: result.errorMessage || null });
        });
        return () => { cancelled = true; };
    }, [userInfo, jobId]);

    if (state.loading) return <p className="client-debug-muted">Loading timeline…</p>;
    if (state.error) return <div className="client-debug-load-error">{state.error}</div>;
    return state.timeline ? <JobTimeline timeline={state.timeline} /> : null;
}

/**
 * Everything Client Debug did for one device: its debugs newest first, each with its lifecycle and the logs it picked
 * up. Opened from a device in the activity table, or by typing a device ID.
 */
export function DeviceTimeline({ userInfo, deviceId: requestedDevice }) {
    const [input, setInput] = useState(requestedDevice || '');
    const [deviceId, setDeviceId] = useState(requestedDevice || '');
    const [days, setDays] = useState(30);
    const [state, setState] = useState({ loading: false, device: null, error: null });

    useEffect(() => {
        if (requestedDevice) {
            setInput(requestedDevice);
            setDeviceId(requestedDevice);
        }
    }, [requestedDevice]);

    const load = useCallback(async () => {
        if (!deviceId) return;
        setState({ loading: true, device: null, error: null });
        const result = await fetchClientDebugDeviceTimeline({ userEmail: userInfo, deviceId, days });
        setState({ loading: false, device: result.device, error: result.errorMessage || null });
    }, [userInfo, deviceId, days]);

    useEffect(() => { load(); }, [load]);

    const jobs = asList(state.device?.jobs);
    const counts = jobs.reduce((sum, job) => ({ ...sum, [outcomeOf(job)]: (sum[outcomeOf(job)] || 0) + 1 }), {});

    return (
        <section className="cdt-device" aria-label="Device Timeline">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Device Timeline</h2>
                    <p>Every debug for a device: when it was requested, which logs it picked up, and how it progressed.</p>
                </div>
                <div className="client-debug-range-controls" role="group" aria-label="Device timeline range">
                    {RANGES.map((range) => (
                        <button
                            type="button"
                            key={range}
                            className={`client-debug-range-button${days === range ? ' active' : ''}`}
                            aria-pressed={days === range}
                            onClick={() => setDays(range)}
                        >
                            {range} days
                        </button>
                    ))}
                </div>
            </div>

            <form
                className="cdt-device-form"
                onSubmit={(event) => { event.preventDefault(); setDeviceId(input.trim()); }}
            >
                <input
                    aria-label="Device ID"
                    placeholder="Device ID"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                />
                <button type="submit" className="client-debug-refresh" disabled={!input.trim() || state.loading}>
                    {state.loading ? 'Loading…' : 'Show timeline'}
                </button>
            </form>

            {state.error && <div className="client-debug-load-error">{state.error}</div>}
            {!state.loading && !state.error && state.device && jobs.length === 0 && (
                <div className="client-debug-empty">No debugs for this device in the last {days} days.</div>
            )}
            {jobs.length > 0 && (
                <p className="cdt-device-summary">
                    {jobs.length} debug{jobs.length === 1 ? '' : 's'} in the last {days} days:{' '}
                    {counts.SUCCESS || 0} success · {counts.FAILED || 0} failure · {counts.IN_PROGRESS || 0} in progress
                </p>
            )}
            {jobs.map((job) => {
                const outcome = outcomeOf(job);
                const note = outcomeNote(job);
                return (
                    <article className="cdt-job-card" key={job.jobId}>
                        <div className="cdt-job-head">
                            <h3>{formatTimestamp(job.createdAt)}</h3>
                            <code title={job.jobId}>{String(job.jobId).slice(0, 8)}</code>
                            <span>{playbookLabel(job.playbook)}</span>
                            <span className={`client-debug-status ${String(job.status || '').toLowerCase()}`}>{OUTCOME_LABELS[outcome]}</span>
                            {note && <span>{note}</span>}
                            {job.durationMs != null && <span>took {formatDuration(job.durationMs)}</span>}
                        </div>
                        <JobTimeline timeline={job} />
                        <LogsPickedUp logs={job.logs} />
                    </article>
                );
            })}
        </section>
    );
}
